/**
 * Registro do service worker e aviso de versao nova.
 *
 * A regra que manda aqui: **nunca recarregar por baixo dela**. Uma pessoa a meio
 * de ditar uma despesa que ve a tela reiniciar sozinho perde o que estava a
 * fazer e deixa de confiar no aplicativo. Por isso o service worker novo fica em
 * espera (`skipWaiting: false` no vite.config.ts) e so troca quando ela tocar em
 * "Atualizar".
 *
 * Em ambientes sem `serviceWorker` (testes, http simples, iOS antigo) tudo isto
 * e um no-op silencioso — o aplicativo continua a funcionar, apenas sem
 * atualizacao automatica.
 */
import { useCallback, useEffect, useState } from 'react';

const SW_URL = '/sw.js';
const SKIP_WAITING = 'SKIP_WAITING';

export interface ServiceWorkerUpdate {
  /** Ha uma versao nova a espera de autorizacao. */
  updateReady: boolean;
  /** O aplicativo ja esta salva para funcionar sem internet. */
  offlineReady: boolean;
  /** Aplica a versao nova e recarrega. So por toque explicito. */
  applyUpdate(): void;
  dismiss(): void;
}

function supported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined'
  );
}

export function useServiceWorkerUpdate(): ServiceWorkerUpdate {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [offlineReady, setOfflineReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!supported() || import.meta.env.DEV) return;
    let cancelled = false;

    const track = (registration: ServiceWorkerRegistration): void => {
      if (registration.active) setOfflineReady(true);
      if (registration.waiting) setWaiting(registration.waiting);

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          // `controller` presente significa que ja havia uma versao a servir:
          // logo isto e uma atualizacao, nao a primeira instalacao.
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            if (!cancelled) setWaiting(installing);
          }
          if (installing.state === 'activated' && !cancelled) setOfflineReady(true);
        });
      });
    };

    navigator.serviceWorker
      .register(SW_URL, { scope: '/' })
      .then((registration) => {
        if (cancelled) return;
        track(registration);
        // Uma verificacao por hora chega: mais do que isso gasta dados moveis.
        const timer = setInterval(() => void registration.update(), 60 * 60 * 1000);
        return () => clearInterval(timer);
      })
      .catch(() => {
        // Sem service worker o aplicativo funciona; so nao funciona offline.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (!waiting) return;
    // Trocar so depois de o novo assumir o controlo, senao recarregamos para a
    // versao antiga e a pessoa fica a pensar que o botao nao fez nada.
    const onControllerChange = (): void => {
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, {
      once: true,
    });
    waiting.postMessage({ type: SKIP_WAITING });

    // Rede de seguranca: se o service worker nao responder em 3 s, recarregamos
    // na mesma. Um botao que nao faz nada e pior do que um recarregamento.
    window.setTimeout(() => window.location.reload(), 3000);
  }, [waiting]);

  return {
    updateReady: waiting !== null && !dismissed,
    offlineReady,
    applyUpdate,
    dismiss: () => setDismissed(true),
  };
}
